import { makeAutoObservable } from "mobx";

import Persistent from "../interfaces/Persistent";
import Store from "../interfaces/Store";

type EmojiMap = {
    [key: string]: number;
};

type EmojiData = {
    chosen: EmojiMap;
    mostUsed: EmojiMap;
};

const MOST_RELEVANT_EMOJIS_MAX = 5;

export class Emojis implements Store, Persistent<EmojiData> {
    mostUsed = new MostUsedEmojis();
    chosen = new ChosenEmojis();

    constructor() {
        makeAutoObservable(this);
    }

    get mostRelevantEmojis() {
        return this.mostUsed.list.slice(0, MOST_RELEVANT_EMOJIS_MAX);
    }

    get id(): string {
        return "emojis";
    }

    toJSON(): EmojiData {
        return {
            mostUsed: this.mostUsed.toJSON(),
            chosen: this.chosen.toJSON(),
        };
    }

    hydrate(data: EmojiData): void {
        this.mostUsed.hydrate(data?.mostUsed);
        this.chosen.hydrate(data?.chosen);
    }
}

type Emoji = string;

type EmojiTimestamp = number;

class ChosenEmojis implements Store, Persistent<EmojiMap> {
    private emojis: Map<Emoji, EmojiTimestamp>;

    constructor() {
        this.emojis = new Map();
        makeAutoObservable(this);
    }

    get id(): string {
        return "emojis:chosen";
    }

    get list() {
        const sorted = [...this.emojis].sort(sortEmojiEntriesByUsage);
        return sorted.map(([emoji]) => emoji);
    }

    has(emoji: string) {
        return this.emojis.has(emoji);
    }

    add(emoji: string) {
        this.emojis.set(emoji, Date.now());
    }

    delete(emoji: string) {
        this.emojis.delete(emoji);
    }

    hydrate(data?: EmojiMap): void {
        // Map хранит порядок ключей а объект нет, так что надо их отсортировать перед помещением в Map
        const entries = Object.entries(data || {});
        this.emojis.clear();

        for (const [emoji, usage] of entries.sort(sortEmojiEntriesByUsage)) {
            this.emojis.set(emoji, usage);
        }
    }

    toJSON(): EmojiMap {
        return Object.fromEntries(this.emojis);
    }
}

function sortEmojiEntriesByUsage(a: EmojiEntry, b: EmojiEntry) {
    return b[1] - a[1];
}

type EmojiRank = number;

type EmojiEntry = [Emoji, EmojiRank];

const MAX_MOST_USED_EMOJIS_LENGTH = 10;

class MostUsedEmojis implements Store, Persistent<EmojiMap> {
    private emojiList: EmojiEntry[];

    constructor() {
        this.emojiList = [];
        makeAutoObservable(this);
    }

    get id(): string {
        return "emojis:most-used";
    }

    get list() {
        return this.emojiList.map(([emojiName]) => emojiName);
    }

    hydrate(data?: EmojiMap): void {
        this.emojiList.length = 0;
        for (const entry of Object.entries(data ?? {}).toSorted(
            sortEmojiEntriesByUsage,
        )) {
            this.emojiList.push(entry);
        }
    }

    increaseRank(emojiName: string) {
        let entryIndex = this.emojiList.findIndex(
            ([name]) => name === emojiName,
        );
        if (entryIndex === -1) {
            entryIndex = this.emojiList.length;
            this.emojiList.push([emojiName, 0]);
            return;
        }

        const emojiEntry = this.emojiList[entryIndex];

        emojiEntry[1] += 1;
        const leftIndex = entryIndex - 1;
        if (leftIndex < 0) {
            return;
        }

        const leftEntry = this.emojiList[leftIndex];
        // Если слева количество использований больше то ничего не делаем
        if (leftEntry[1] > emojiEntry[1]) {
            return;
        }

        const tmp = this.emojiList[leftIndex];
        this.emojiList[leftIndex] = this.emojiList[entryIndex];
        this.emojiList[entryIndex] = tmp;
    }

    toJSON(): EmojiMap {
        let currentMaxEmojiRank = MAX_MOST_USED_EMOJIS_LENGTH + 1;
        const max = this.emojiList.slice(0, MAX_MOST_USED_EMOJIS_LENGTH);
        const out: EmojiEntry[] = [];
        for (const [emoji] of max) {
            out.push([emoji, currentMaxEmojiRank]);
            currentMaxEmojiRank -= 1;
        }
        return Object.fromEntries(out);
    }
}
